import type { LogtoConfig } from '@logto/react';

/**
 * Logto OIDC configuration for the admin portal (shadow mode).
 *
 * The SPA app is registered in Logto (sso.sys9.co) as a public client.
 * `VITE_LOGTO_APP_ID` must be set per-environment; the endpoint falls back
 * to the production Logto tenant.
 */
export const logtoConfig: LogtoConfig = {
  endpoint: import.meta.env.VITE_LOGTO_ENDPOINT || 'https://sso.sys9.co',
  appId: import.meta.env.VITE_LOGTO_APP_ID || '',
  scopes: [
    'openid',
    'profile',
    'email',
    'offline_access',
    'urn:logto:scope:organization_roles',
    'urn:logto:scope:organizations',
    // Global (non-organization) roles, e.g. platform-admin.
    'roles',
  ],
};

/** Absolute redirect URI registered on the Logto application. */
export const logtoRedirectUri = `${window.location.origin}/auth/callback`;

/** True when an app id is configured; hides/disables SSO UI otherwise. */
export const isLogtoConfigured = Boolean(import.meta.env.VITE_LOGTO_APP_ID);

/**
 * Dev-auth mode predicate.
 *
 * The admin portal normally authenticates exclusively via Logto SSO.
 * For local development, MSW mock mode, and browser E2E runs we support a
 * direct username/password login against POST /api/v1/auth/login
 * (served by MSW in mock mode, or a locally seeded backend).
 *
 * Dev-auth mode is active only when the bundle was built/served with:
 *   - VITE_AUTH_MODE=dev            (explicit opt-in), or
 *   - VITE_USE_MOCK_API=true        (MSW mock mode — implies dev auth)
 *
 * Production builds set neither variable, so this evaluates to `false` and
 * the Logto-only flow (with login() throwing) is preserved. Vite inlines
 * `import.meta.env.*` at build time, so the branch cannot be toggled at
 * runtime in a production bundle.
 */
export const isDevAuthMode = (): boolean =>
  import.meta.env.VITE_AUTH_MODE === 'dev' ||
  import.meta.env.VITE_USE_MOCK_API === 'true';
